<template>
    <Headify :data="headMeta" />

    <div class="min-h-screen bg-white">
        <NavMain :key="route.name" />

        <div class="flex flex-1 flex-col lg:pl-64">
            <main class="flex-1">
                <div class="mx-auto w-full max-w-7xl px-2 pt-12 sm:px-4 lg:pt-4">
                    <div class="py-2">
                        <Breadcrumbs :links="breadcrumbsLinks" />
                        <BatchImportSteps :steps="wizzardSteps" />
                    </div>

                    <div class="mt-4 border border-dashed border-gray-300 p-4">
                        <div
                            v-if="payload.data.length === 0"
                            id="editor-wrapper"
                            class="flex min-h-[200px]"
                        >
                            <div
                                class="flex w-full cursor-pointer items-center justify-center text-gray-200 hover:text-gray-300"
                                @click="chooseFile()"
                            >
                                <MdiCloudUpload class="h-24 w-24" />
                                <input
                                    id="dropzone-file"
                                    type="file"
                                    class="hidden"
                                    accept="application/json,text/csv"
                                    multiple
                                    @change="uploadFiles()"
                                />
                            </div>
                        </div>

                        <div
                            v-if="payload.data.length > 0"
                            class="preview-area min-h-[200px] divide-y divide-solid divide-gray-200 text-sm"
                        >
                            <div
                                v-for="(file, index) in payload.data"
                                :key="index"
                                class="py-4 text-left sm:flex"
                            >
                                <div
                                    class="flex flex-none items-start justify-start px-1 pt-2 text-sm select-none"
                                >
                                    <span
                                        v-if="file.validFileFormat && file.srcType === 'json'"
                                        v-tippy="t('labelValidJSON')"
                                        class="inline-block w-20 rounded-sm bg-green-400 px-2 py-1 font-medium text-white"
                                    >
                                        <MdiCheckCircle class="-mt-1 mr-1 inline h-4 w-4" />
                                        {{ t('labelJSON') }}
                                    </span>
                                    <span
                                        v-if="!file.validFileFormat && file.srcType === 'json'"
                                        v-tippy="t('labelInvalidJSON')"
                                        class="inline-block w-20 rounded-sm bg-rose-400 px-2 py-1 font-medium text-white"
                                    >
                                        <MdiAlertCircle class="-mt-1 mr-1 inline h-4 w-4" />
                                        {{ t('labelJSON') }}
                                    </span>
                                    <span
                                        v-if="file.validFileFormat && file.srcType === 'csv'"
                                        v-tippy="t('labelValidCSV')"
                                        class="inline-block w-20 rounded-sm bg-green-400 px-2 py-1 font-medium text-white"
                                    >
                                        <MdiCheckCircle class="-mt-1 mr-1 inline h-4 w-4" />
                                        {{ t('labelCSV') }}
                                    </span>
                                    <span
                                        v-if="!file.validFileFormat && file.srcType === 'csv'"
                                        v-tippy="t('labelInvalidCSV')"
                                        class="inline-block w-20 rounded-sm bg-rose-400 px-2 py-1 font-medium text-white"
                                    >
                                        <MdiAlertCircle class="-mt-1 mr-1 inline h-4 w-4" />
                                        {{ t('labelCSV') }}
                                    </span>
                                </div>
                                <div
                                    class="flex flex-none items-start justify-start px-1 pt-2 text-sm select-none"
                                >
                                    <span
                                        v-if="file.valid"
                                        v-tippy="t('labelValidDataset')"
                                        class="inline-block rounded-sm bg-green-400 px-2 py-1 font-medium text-white"
                                    >
                                        <MdiCheckCircle class="-mt-1 mr-1 inline h-4 w-4" />
                                        {{ t('labelAPI') }}
                                    </span>
                                    <span
                                        v-if="!file.valid"
                                        v-tippy="t('labelInvalidDataset')"
                                        class="inline-block rounded-sm bg-rose-400 px-2 py-1 font-medium text-white"
                                    >
                                        <MdiAlertCircle class="-mt-1 mr-1 inline h-4 w-4" />
                                        {{ t('labelAPI') }}
                                    </span>
                                </div>
                                <div class="flex-1 px-2 pt-0.5 break-all">
                                    <span class="text-base font-medium"
                                        >{{ t('File') }}: {{ file.src.name }}</span
                                    >
                                    <p class="py-1 break-all">
                                        {{ file.message }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div
                            class="flex flex-col gap-2 px-1 py-2 text-right select-none sm:flex-row sm:items-center sm:justify-end sm:p-4"
                        >
                            <ActionButton
                                :key="`${componentKey}0`"
                                :value="t('buttonReset')"
                                state="warning"
                                :is-disabled="payload.data.length === 0"
                                :icon="MdiDelete"
                                @click="reset()"
                            />
                            <ActionButton
                                :key="`${componentKey}1`"
                                :value="t('buttonPreview')"
                                state="info"
                                :is-disabled="saving.finishedValidation === false"
                                :icon="MdiListStatus"
                                @click="previewFiles()"
                            />
                            <ActionButton
                                :key="`${componentKey}2`"
                                :value="t('buttonSend')"
                                state="primary"
                                :is-disabled="saving.finishedValidation === false"
                                :icon="MdiCubeSend"
                                @click="sendFiles()"
                            />
                        </div>
                    </div>
                </div>
            </main>
        </div>
    </div>

    <ModalPrompt
        :state="confirmModal.state"
        :buttons="confirmModal.buttons"
        :icon="confirmModal.icon"
    />

    <Spinner
        v-if="saving.active"
        type="dots"
        :color="`text-blue-600`"
        :color2="`text-blue-400`"
        :text-color="`text-gray-200`"
        :overlay="true"
        :speed="1000"
        :dots="true"
        :dot-char="'.'"
        :dot-dual="false"
        :dot-reverse="false"
        :text="saving.text"
        :inline="false"
        :pulse="true"
    />
</template>
<script setup>
    import { reactive, ref, watch } from 'vue';
    import { useRoute } from 'vue-router';
    import Headify from '../components/Headify.vue';
    import Spinner from '../ui/Spinner.vue';
    import Breadcrumbs from '../ui/Breadcrumbs.vue';
    import NavMain from '../ui/NavMain.vue';
    import ModalPrompt from '../ui/ModalPrompt.vue';
    import navs from '../composables/navs';
    import { t } from '../composables/i18n';
    import slugify from 'slugify';
    import { fileExtension, asleep, getRndKey } from '../composables/utils';
    import { validate, process } from '../composables/useMultidocument';
    import { CSVToObject } from '../composables/useCSV';
    import BatchImportSteps from '../components/BatchImportSteps.vue';
    import ActionButton from '../ui/ActionButton.vue';
    import {
        MdiCheckCircle,
        MdiAlertCircle,
        MdiCloudUpload,
        MdiCubeSend,
        MdiDelete,
        MdiListStatus,
        MdiCheck,
    } from 'materialdesignicons-vue3/icons/';

    const route = useRoute();
    const componentKey = ref(getRndKey());
    const breadcrumbsLinks = [
        {
            name: 'dashboard',
            title: t('Dashboard'),
        },
        {
            title: t('BatchImport'),
            final: true,
        },
    ];

    const payload = reactive({
        data: [],
    });

    const checker = reactive({
        formatValidationDone: false,
    });

    const saving = reactive({
        active: false,
        text: t('loading'),
        progress: 0,
        finishedJSON: false,
        finishedValidation: false,
    });

    const confirmModal = reactive({
        state: {
            open: false,
            title: t('multipleUploadFilesSentTitle'),
            text: t('multipleUploadFilesSent'),
            data: {},
            overlayPreventClose: true,
        },
        icon: MdiListStatus,
        buttons: [
            {
                title: t('buttonOk'),
                action: () => {
                    confirmModal.state.open = false;
                },
                state: 'success',
                icon: MdiCheck,
            },
        ],
    });

    const templateid = route.params.templateid;
    const templateversionid = route.params.templateversionid;

    const wizzardSteps = [
        {
            id: '01',
            title: t('Templates'),
            path: {
                name: 'batch-import-1',
            },
            status: 'complete',
        },
        {
            id: '02',
            title: t('TemplateVersions'),
            path: {
                name: 'batch-import-2',
                params: {
                    templateid: templateid,
                },
            },
            status: 'complete',
        },
        {
            id: '03',
            title: t('buttonSchema'),
            path: {
                name: 'batch-import-3',
                params: {
                    templateid: templateid,
                    templateversionid: templateversionid,
                },
            },
            status: 'complete',
        },
        {
            id: '04',
            title: t('processFiles'),
            href: 'batch-import-4',
            status: 'current',
        },
    ];

    const headMeta = navs().metaFind(route.name);

    // File operations
    const chooseFile = function () {
        document.querySelector('#dropzone-file').click();
    };

    const uploadFiles = async (fileInputId = '#dropzone-file') => {
        const filesSrc = document.querySelector(fileInputId).files;
        saving.active = true;

        for (let i = 0; i < filesSrc.length; i++) {
            saving.progress = ((100 / filesSrc.length) * i).toFixed(0);
            saving.text = `${t('validating')} ${saving.progress.toString().padStart(3, '\xa0')}%`;
            if (['json'].includes(fileExtension(filesSrc[i].name).toLowerCase())) {
                await validateJSON(i, filesSrc[i]);
            }
            if (['csv'].includes(fileExtension(filesSrc[i].name).toLowerCase())) {
                await validateCSV(i, filesSrc[i]);
            }
            if (i === filesSrc.length - 1) {
                await asleep(1000);
                saving.finishedJSON = true;
                checker.formatValidationDone = true;
            }
        }
    };

    const validateJSON = async (index, input) => {
        const readFile = new FileReader();
        const _payloadItem = {
            validFileFormat: false,
            valid: false,
            message: 'Initial message',
            json: [],
            src: input,
            srcType: 'json',
        };

        readFile.onload = (e) => {
            const contents = e.target.result;

            try {
                const json = JSON.parse(contents);
                _payloadItem.validFileFormat = true;
                _payloadItem.json = json;
                _payloadItem.message = t('labelValidJSON');
            } catch (e) {
                _payloadItem.valid = false;
                _payloadItem.message = t('labelInvalidJSON');
            }
            payload.data.push(_payloadItem);
            console.log('_payloadItem', _payloadItem);
            return _payloadItem;
        };
        readFile.readAsText(input);
    };

    const validateCSV = async (index, input) => {
        const readFile = new FileReader();
        const _payloadItem = {
            validFileFormat: false,
            valid: false,
            message: 'Initial message',
            json: [],
            src: input,
            srcType: 'csv',
        };

        readFile.onload = (e) => {
            const contents = e.target.result;

            try {
                const json = CSVToObject(contents);
                _payloadItem.validFileFormat = true;
                _payloadItem.json = json;
                _payloadItem.message = t('labelValidCSV');
            } catch (e) {
                _payloadItem.valid = false;
                _payloadItem.message = t('labelInvalidCSV');
            }
            payload.data.push(_payloadItem);
            return _payloadItem;
        };
        readFile.readAsText(input);
    };

    // API
    const validateBatchOnline = async (_payload) => {
        let canSendFiles = true;
        console.log('_payload', _payload);
        for (let file in _payload.data) {
            saving.progress = ((100 / _payload.data.length) * file).toFixed(0);
            saving.text = `${t('validating')} ${saving.progress.toString().padStart(3, '\xa0')}%`;
            if (_payload.data[file].validFileFormat === true) {
                const JSONResult = await validate(_payload.data[file].json);
                if (JSONResult === false) {
                    canSendFiles = false;
                    _payload.data[file].valid = false;
                    _payload.data[file].message = `${t('labelInvalidDataset')}, ${t('items', {
                        count: _payload.data[file].json.length,
                    })}`;
                } else {
                    _payload.data[file].valid = true;
                    _payload.data[file].message = `${t('labelValidDataset')}, ${t('items', {
                        count: _payload.data[file].json.length,
                    })}, ${_payload.data[file].json[0].templateCode}`;
                }
            } else {
                canSendFiles = false;
            }
        }
        saving.finishedValidation = canSendFiles;
    };

    const processBatchOnline = async (_payload) => {
        saving.active = true;
        console.log('_payload', _payload);
        for (let file in _payload.data) {
            saving.progress = ((100 / _payload.data.length) * file).toFixed(0);
            saving.text = `${t('sending')} ${saving.progress.toString().padStart(3, '\xa0')}%`;
            if (_payload.data[file].valid === true) {
                const fileResult = {
                    data: '',
                    fileName: `TEMPLATING-BATCH-PREVIEW-${slugify(
                        _payload.data[file].json[0].templateCode.toLowerCase(),
                    )}.zip`,
                };
                fileResult.data = await process(_payload.data[file].json, true);

                if (fileResult.data !== false) {
                    saving.inter = true;

                    await downloadFile(fileResult.data, fileResult.fileName);
                    await asleep(1000);
                }
            }
        }
        saving.active = false;
    };

    // Process
    const downloadFile = async (content, fileName) => {
        const dataUrl = window.URL.createObjectURL(content);
        const link = document.createElement('a');
        link.href = dataUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const previewFiles = async () => {
        saving.active = true;
        saving.text = t('sending');
        await processBatchOnline(payload);
        return true;
    };

    const sendFiles = async () => {
        saving.active = true;
        saving.text = t('sending');
        await asleep(3000);
        //await --- SOME API CALL ---
        saving.active = false;
        confirmModal.state.open = true;
        return true;
    };

    const reset = async () => {
        saving.finishedJSON = false;
        saving.finishedValidation = false;
        saving.progress = 0;
        saving.text = t('loading');
        payload.data = [];
        saving.active = false;
        checker.formatValidationDone = false;
    };

    // Watchers
    watch(checker, async () => {
        if (checker.formatValidationDone === true) {
            await validateBatchOnline(payload);
            saving.active = false;
        }
    });
</script>
